import React, { useMemo } from 'react';

// Components
import RiskProfilesTable from '@/views/components/tables/RiskProfilesTable';
import { withErrorBoundary } from '@/views/components/errors';
import { ContactDetailsRiskProfilesHeader } from './components/ContactDetailsRiskProfilesHeader';

// Libraries
import { connect } from 'react-redux';

// Hooks
import { useRiskProfileSearch } from './hooks/useRiskProfileSearch';

// Types
import ContactRiskProfile from '@/types/contact-risk-profile';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Locale from '@/types/locale';
import { CurrentContact } from '@/redux/modules/types/contacts';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { sortByDateKey } from '@/utils/dates';

type StateProps = {
  locale: Locale;
  riskProfiles: ContactRiskProfile[];
};

type Props = StateProps & {
  currentContact: CurrentContact;
  loading: boolean;
  onAttachRiskProfile: (profile: ContactRiskProfile) => void;
  onCreateRiskProfile: (questionnaire?: KycQuestionnaire) => void;
  onDeleteRiskProfile: (profile: ContactRiskProfile) => void;
  onEditRiskProfile: (profile: ContactRiskProfile) => void;
};

const ContactDetailsRiskProfiles = (props: Props) => {
  const { currentContact, loading, locale, onAttachRiskProfile, onCreateRiskProfile, onDeleteRiskProfile, onEditRiskProfile, riskProfiles } = props;
  const data: ContactRiskProfile[] = useMemo(() => riskProfiles, [riskProfiles]);

  const { query, searching, tableData, onSearch } = useRiskProfileSearch<ContactRiskProfile>({
    data,
    locale,
  });

  return (
    <div className="contact-details-risk-profiles table-wrapper table-container">
      <ContactDetailsRiskProfilesHeader
        currentContact={currentContact}
        loading={loading}
        searching={searching}
        query={query}
        onSearch={onSearch}
        onCreateRiskProfile={onCreateRiskProfile}
      />

      <div className="contact-details-risk-profile-body">
        <RiskProfilesTable
          data={tableData}
          onAttach={onAttachRiskProfile}
          onCreate={onCreateRiskProfile}
          onDelete={onDeleteRiskProfile}
          onEdit={onEditRiskProfile}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const {
    contacts: { current },
    intl: { locale },
  } = state;

  return {
    locale,
    riskProfiles: current?.contact.riskProfiles?.slice().sort(sortByDateKey) || [],
  };
};

export default connect(mapStateToProps)(withErrorBoundary(ContactDetailsRiskProfiles));
